import { BILLING_PATH, billingPathFor, parseBillingOrgID, parseSetupMarker } from 'library/helpers/billingContact';
import { stashOrgID } from 'library/supportAccess/pendingOrg';

/**
 * Carries "come back to Billing for org N" across a sign-in round-trip (Kinde leaves our origin and returns to
 * /callback with nothing of ours in the URL).
 *
 * 🛑 ONLY AN ORG ID AND A SETUP MARKER ARE STORED — never a path, a URL or a token. The path returned is rebuilt by
 * `billingPathFor`, so the only place a sign-in can ever be sent is `/access/billing` (audit C1: fixed, allowlisted
 * return paths). The org choice is also handed to the existing `?org=` deep-link stash, so the profile bootstrap
 * selects that org — and, as there, only if it is in the signed-in person's own list.
 */
const KEY = 'pendingBillingReturn';
const TTL_MS = 30 * 60 * 1000;

export const stashBillingReturn = (orgID, setup = null) => {
	try {
		const id = Number(orgID);
		if (!Number.isSafeInteger(id) || id <= 0) return;
		localStorage.setItem(KEY, JSON.stringify({ orgID: id, setup: setup || null, createdAt: Date.now() }));
		stashOrgID(id);
	} catch {
		// Storage unavailable: after sign-in the person lands on the dashboard and Billing is in the sidebar.
	}
};

/** The Billing path to return to, if one is stashed and fresh. Cleared either way: used at most once. */
export const takeBillingReturn = () => {
	try {
		const raw = localStorage.getItem(KEY);
		localStorage.removeItem(KEY);
		if (!raw) return null;
		const { orgID, setup, createdAt } = JSON.parse(raw);
		if (!Number.isSafeInteger(orgID) || orgID <= 0 || typeof createdAt !== 'number') return null;
		if (Date.now() - createdAt > TTL_MS) return null;
		return billingPathFor(orgID, setup);
	} catch {
		return null;
	}
};

/** Drops any stashed return without using it (the Billing page has been reached). */
export const clearBillingReturn = () => {
	try {
		localStorage.removeItem(KEY);
	} catch {
		// nothing to do
	}
};

/**
 * Boot: when the page loads ON a Billing URL, remember it. A signed-out visitor is about to be sent to sign-in (which
 * drops the query); a signed-in one boots straight into that org via the org stash. The Billing page clears the return
 * stash on mount, so a signed-in boot leaves nothing behind.
 */
export const stashBillingReturnFromLocation = (loc = window.location) => {
	try {
		if (loc.pathname !== BILLING_PATH && loc.pathname !== `${BILLING_PATH}/`) return;
		const orgID = parseBillingOrgID(loc.search);
		if (orgID) stashBillingReturn(orgID, parseSetupMarker(loc.search));
	} catch {
		// malformed location: nothing to remember
	}
};
